import { useHeaderCollapse } from "@portal/hooks/useHeaderCollapse";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export const BOND_REQUEST_TAB_IDS = ["overview", "bond-form", "pel-pml"] as const;

export type BondRequestTabId = (typeof BOND_REQUEST_TAB_IDS)[number];

export const DEFAULT_BOND_REQUEST_TAB: BondRequestTabId = "overview";

const isBondRequestTabId = (value: string | null): value is BondRequestTabId =>
	value !== null && BOND_REQUEST_TAB_IDS.some((id) => id === value);

export const useBondRequestTabNavigation = () => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const [searchParams] = useSearchParams();
	const { headerCollapsed, setHeaderCollapsed, getHeaderCollapsed } = useHeaderCollapse();

	const rawTabId = searchParams.get("t");
	const activeTabId = useMemo<BondRequestTabId>(
		() => (isBondRequestTabId(rawTabId) ? rawTabId : DEFAULT_BOND_REQUEST_TAB),
		[rawTabId],
	);

	const setActiveTabId = useCallback(
		(tabId: string) => {
			const next = new URLSearchParams(search);
			next.set("t", tabId);
			const query = next.toString();
			navigate({ pathname, search: query ? `?${query}` : "" }, { replace: true });
		},
		[navigate, pathname, search],
	);

	return {
		activeTabId,
		setActiveTabId,
		headerCollapsed,
		setHeaderCollapsed,
		getHeaderCollapsed,
	};
};
